import React, { useEffect, useRef, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import ShareIcon from '@atlaskit/icon/core/share';

import { useSessionData } from '@confluence/session-data';
import type { FlagsStateContainer } from '@confluence/flags';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController';
import { useShareAndRestrictButtonState } from '@confluence/share-and-restrict-dialog/entry-points/useShareAndRestrictButtonState';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import { useCopyContentLinkState } from '@confluence/copy-content-link/entry-points/useCopyContentLinkState';
import { expValEquals } from '@confluence/feature-experiments';

export type UseShareNewWhiteboardFlagProps = {
	isNewWhiteboard: boolean;
	isMobile: boolean;
	contentId: string;
	spaceKey: string;
} & {
	dialogs: DialogsStateContainer;
	flags: FlagsStateContainer;
};

export const SHARE_NEW_WHITEBOARD_FLAG_NAME = 'shareNewWhiteboardFlag';
const FIVE_MINUTES_TIMEOUT = 300000;

const i18n = defineMessages({
	shareFlagTitle: {
		id: 'experiment-share-new-whiteboard.flag-title',
		description: 'Title displayed on share flag. Optimal characters is 10 or less',
		defaultMessage: 'Share this whiteboard with your team',
	},
	shareFlagDescription: {
		id: 'experiment-share-new-whiteboard.flag-description',
		description: 'Description displayed on share flag. Optimal characters is 30 or less',
		defaultMessage:
			'Start collaborating in real-time using tools like voting, timer, integrations, and more.',
	},
	shareFlagCTA: {
		id: 'experiment-share-new-whiteboard.flag-cta',
		description: 'The button text to share whiteboard. Optimal characters is 10 or less',
		defaultMessage: 'Share whiteboard',
	},
});

export function useShareNewWhiteboardFlag({
	isNewWhiteboard,
	isMobile = false,
	contentId,
	spaceKey,
	dialogs,
	flags,
}: UseShareNewWhiteboardFlagProps) {
	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const shareWhiteboardFlagTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const { hasClickedShareButton } = useShareAndRestrictButtonState();
	const { hasClickedCopyContentLink } = useCopyContentLinkState();

	const handleShowShareAndRestrictDialog = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_NEW_WHITEBOARD_FLAG_NAME,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'ShareWhiteboard',
			},
		}).fire();
		void flags.hideFlag(SHARE_NEW_WHITEBOARD_FLAG_NAME);
		dialogs.showModal(ShareAndRestrictDialogController, {
			contentId,
			contentType: 'whiteboard',
			allowRestrictions: true,
			pageModes: 'view',
			refetchButtonData: () => {},
			isUnpublishedDraft: false,
			spaceKey,
			openFrom: SHARE_NEW_WHITEBOARD_FLAG_NAME,
		});
	}, [createAnalyticsEvent, contentId, spaceKey, dialogs, flags]);

	const handleDismissFlag = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissFlag',
				source: SHARE_NEW_WHITEBOARD_FLAG_NAME,
			},
		}).fire();
	}, [createAnalyticsEvent]);

	//Step 1 : Add timeout to trigger share whiteboard flag
	// -> Only eligible for experiment and new whiteboard will have trigger
	//Step 2: Show share whiteboard flag when 5 mins timeout due
	// -> Only if user hasnt click share button or copy link button can see share whiteboard flag
	// -> Only if user are in test(experiment) group will fire exposure and show share whiteboard flag
	useEffect(() => {
		if (!isMobile && isNewWhiteboard) {
			shareWhiteboardFlagTimeoutRef.current = setTimeout(() => {
				if (!hasClickedShareButton && !hasClickedCopyContentLink) {
					//2 experiments are mutually exclusive on Confluence edition check level
					if (
						(edition === ConfluenceEdition.FREE &&
							expValEquals('cc_share_new_whiteboard_free', 'cohort', 'experiment')) ||
						(edition !== ConfluenceEdition.FREE &&
							expValEquals('cc_share_new_whiteboard_paid', 'cohort', 'experiment'))
					) {
						void flags.showFlag({
							id: SHARE_NEW_WHITEBOARD_FLAG_NAME,
							onClose: handleDismissFlag,
							close: 'never',
							type: 'custom',
							customIcon: <ShareIcon label="" />,
							title: formatMessage(i18n.shareFlagTitle),
							description: formatMessage(i18n.shareFlagDescription),
							actions: [
								{
									content: formatMessage(i18n.shareFlagCTA),
									onClick: () => handleShowShareAndRestrictDialog(),
								},
							],
						});
						createAnalyticsEvent({
							type: 'sendScreenEvent',
							data: {
								name: SHARE_NEW_WHITEBOARD_FLAG_NAME,
							},
						}).fire();
					}
				}
			}, FIVE_MINUTES_TIMEOUT);
		}

		return () => {
			if (shareWhiteboardFlagTimeoutRef.current) {
				clearTimeout(shareWhiteboardFlagTimeoutRef.current);
			}
			void flags.hideFlag(SHARE_NEW_WHITEBOARD_FLAG_NAME);
		};
	}, [
		isNewWhiteboard,
		isMobile,
		hasClickedShareButton,
		hasClickedCopyContentLink,
		handleShowShareAndRestrictDialog,
		handleDismissFlag,
		createAnalyticsEvent,
		flags,
		formatMessage,
		edition,
	]);
}
